import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { experienciasComplementares } from '../../../mocks/experiencias-complementares';
import { createStripeCheckout, getPriceIdFromSupabase } from '../../../lib/stripeCheckout';

export default function ExperienciaDetalhes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const experiencia = experienciasComplementares.find(exp => exp.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.observe-animation').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [experiencia]);

  if (!experiencia) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Experiência não encontrada</h1>
          <Link to="/planos" className="text-[#D4AF37] hover:underline">
            Voltar para Planos
          </Link>
        </div>
      </div>
    );
  }

  const experienciasRelacionadasData = experienciasComplementares.filter(
    exp => experiencia.experienciasRelacionadas.includes(exp.id)
  );

  const handleComprar = async () => {
    setIsLoading(true);
    try {
      const priceId = await getPriceIdFromSupabase(experiencia!.id);
      const checkoutUrl = await createStripeCheckout({
        priceId,
        mode: 'payment',
        successUrl: `${window.location.origin}/planos/sucesso?experiencia=${experiencia!.id}`,
        cancelUrl: window.location.href,
        metadata: { experienciaId: experiencia!.id, experienciaTitulo: experiencia!.titulo },
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Erro ao criar sessão de checkout:', error);
      alert('Erro ao processar pagamento. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={experiencia.imagemHero}
            alt={experiencia.titulo}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="inline-block px-5 py-2 bg-[#D4AF37]/20 backdrop-blur-sm border border-[#D4AF37]/30 rounded-full mb-6">
              <span className="text-[#D4AF37] text-base font-medium tracking-wide">
                {experiencia.categoria}
              </span>
            </div>

            <h1 className="text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {experiencia.titulo}
            </h1>

            <p className="text-2xl text-gray-200 mb-8 leading-relaxed">
              {experiencia.subtitulo}
            </p>

            <div className="flex items-center gap-8 mb-8">
              <div>
                <p className="text-gray-300 text-base mb-2">Investimento</p>
                <p className="text-5xl font-bold text-[#D4AF37]">
                  R$ {experiencia.preco}
                </p>
              </div>
            </div>

            <button
              onClick={handleComprar}
              disabled={isLoading}
              className="px-10 py-5 bg-[#D4AF37] text-black text-lg font-semibold rounded-lg hover:bg-[#C4A137] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isLoading ? 'Processando...' : 'Adquirir Agora'}
            </button>
          </div>
        </div>
      </section>

      {/* Sobre Esta Experiência */}
      <section className="py-20 observe-animation opacity-0">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sobre Esta Experiência
          </h2>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              {experiencia.descricao}
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              {experiencia.descricaoDetalhada}
            </p>
          </div>

          {experiencia.meditacaoRecomendada && (
            <div className="mt-12 p-8 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-music-2-line text-2xl text-[#D4AF37]"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {experiencia.meditacaoRecomendada.titulo}
                  </h3>
                  <p className="text-lg text-gray-600 mb-5 leading-relaxed">
                    {experiencia.meditacaoRecomendada.descricao}
                  </p>
                  <a
                    href={experiencia.meditacaoRecomendada.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C4A137] font-semibold text-lg transition-colors cursor-pointer"
                  >
                    <span>Acessar Meditação</span>
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-20 bg-white observe-animation opacity-0">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Benefícios Exclusivos
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Descubra tudo o que esta experiência pode proporcionar para sua transformação
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {experiencia.beneficios.map((beneficio, index) => (
              <div
                key={index}
                className="p-8 bg-[#FAF8F5] rounded-2xl border border-gray-200 hover:border-[#D4AF37] transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-6">
                  <i className={`${beneficio.icone} text-3xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {beneficio.titulo}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {beneficio.descricao}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* O Que Você Vai Receber */}
      <section className="py-20 observe-animation opacity-0">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              O Que Você Vai Receber
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Conteúdo completo e detalhado desta experiência transformadora
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10">
            <div className="space-y-5">
              {experiencia.oQueVaiReceber.map((item, index) => (
                <div key={index} className="flex items-start gap-5">
                  <div className="w-10 h-10 bg-[#D4AF37] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                    {index + 1}
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed pt-1.5">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA de Compra */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black observe-animation opacity-0">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Comece Sua Transformação Hoje
          </h2>
          <p className="text-2xl text-gray-300 mb-10 leading-relaxed">
            Invista em você e construa o futuro que você merece viver
          </p>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-10 mb-10">
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-gray-400 text-2xl line-through">R$ {Math.round(experiencia.preco * 1.5)}</span>
              <span className="text-6xl font-bold text-[#D4AF37]">R$ {experiencia.preco}</span>
            </div>

            <button
              onClick={handleComprar}
              disabled={isLoading}
              className="w-full max-w-md mx-auto block px-12 py-6 bg-[#D4AF37] text-black text-xl font-bold rounded-lg hover:bg-[#C4A137] transition-all duration-300 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap mb-8"
            >
              {isLoading ? 'Processando...' : 'Adquirir Agora'}
            </button>

            <div className="flex items-center justify-center gap-8 text-gray-400 text-base">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-xl text-[#D4AF37]"></i>
                <span>Pagamento Seguro</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-lock-line text-xl text-[#D4AF37]"></i>
                <span>Dados Protegidos</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-customer-service-line text-xl text-[#D4AF37]"></i>
                <span>Suporte Dedicado</span>
              </div>
            </div>
          </div>

          <p className="text-gray-400 text-lg">
            Garantia de 30 dias • Acesso imediato após a compra
          </p>
        </div>
      </section>

      {/* Experiências Relacionadas */}
      {experienciasRelacionadasData.length > 0 && (
        <section className="py-20 bg-white observe-animation opacity-0">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Experiências Relacionadas
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Potencialize ainda mais sua jornada com estas experiências complementares
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {experienciasRelacionadasData.map((exp) => (
                <Link
                  key={exp.id}
                  to={`/experiencia/${exp.id}`}
                  className="group bg-[#FAF8F5] rounded-2xl border border-gray-200 overflow-hidden hover:border-[#D4AF37] transition-all duration-300 hover:shadow-xl cursor-pointer"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={exp.imagemHero}
                      alt={exp.titulo}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/90 text-black text-sm font-semibold rounded-full mb-3">
                        {exp.categoria}
                      </span>
                      <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {exp.titulo}
                      </h3>
                    </div>
                  </div>
                  <div className="p-8">
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {exp.subtitulo}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold text-[#D4AF37]">
                        R$ {exp.preco}
                      </span>
                      <span className="text-[#D4AF37] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                        Ver Detalhes
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Eu do Futuro
              </h3>
              <p className="text-gray-400 text-base leading-relaxed">
                Transforme sua vida através do autoconhecimento e da conexão com seu eu futuro.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Navegação</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Início
                  </Link>
                </li>
                <li>
                  <Link to="/planos" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Planos
                  </Link>
                </li>
                <li>
                  <Link to="/sobre" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Sobre
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Suporte</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/faq" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    FAQ
                  </Link>
                </li>
                <li>
                  <a href="mailto:contato@eudofuturo.com" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Contato
                  </a>
                </li>
                <li>
                  <Link to="/privacidade" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Privacidade
                  </Link>
                </li>
                <li>
                  <Link to="/termos" className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer">
                    Termos
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Redes Sociais</h4>
              <div className="flex gap-4">
                <a href="#" className="w-12 h-12 bg-white/5 hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-white/5 hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-white/5 hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all cursor-pointer">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-base">
              © 2024 Eu do Futuro. Todos os direitos reservados.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#D4AF37] transition-colors text-base cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
}